import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';
import { getExtensionId } from './extension';

// Create a singleton instance
let supabaseInstance: ReturnType<typeof createClient<Database>> | null = null;

export const getSupabase = () => {
  if (supabaseInstance) return supabaseInstance;
  
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  supabaseInstance = createClient<Database>(supabaseUrl, supabaseAnonKey);
  return supabaseInstance;
};

export const supabase = getSupabase();

// Handle auth state changes
supabase.auth.onAuthStateChange(async (event, session) => {
  // Add error handling and logging
  try {
    console.log(`Auth state change: ${event}`);
    
    if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
      if (!session) {
        console.log('Auth state change event without session');
        return;
      }

      console.log('Session detected:', {
        userId: session.user.id,
        email: session.user.email,
        hasAccessToken: !!session.access_token,
        hasRefreshToken: !!session.refresh_token,
        accessTokenLength: session.access_token?.length || 0,
        refreshTokenLength: session.refresh_token?.length || 0
      });

      // Get the extension ID from our configuration
      const extensionId = import.meta.env.VITE_EXTENSION_ID || 'cnebnhncpobjmlegjlfhlglmamflnlko';
      console.log(`Using extension ID: ${extensionId}`);
      
      try {
        const authData = {
          type: 'POSTNUDGE_AUTH_SUCCESS',
          extensionId: extensionId,  // This is the key - we send the ID we're configured to use
          userId: session.user.id,
          email: session.user.email,
          token: session.access_token,
          refresh_token: session.refresh_token
        };
        
        console.log('Preparing to send auth data to extension:', {
          type: authData.type,
          extensionId: authData.extensionId,
          userId: authData.userId,
          email: authData.email,
          hasToken: !!authData.token,
          hasRefreshToken: !!authData.refresh_token,
          tokenLength: authData.token?.length || 0,
          refreshTokenLength: authData.refresh_token?.length || 0
        });
        
        window.postMessage(authData, window.location.origin);
        console.log('Sent auth data to extension with ID:', extensionId);
      } catch (e) {
        console.error('Failed to send auth data to extension:', e);
      }
    }
  } catch (error) {
    console.error('Error in auth state change handler:', error);
  }
});

// Listen for responses from the extension
window.addEventListener('message', (event) => {
  // Verify the message origin for security
  if (event.origin !== window.location.origin) return;
  
  const { data } = event;
  if (data && data.type === 'POSTNUDGE_AUTH_RESPONSE') {
    console.log('Received response from extension:', {
      type: data.type,
      success: data.success,
      message: data.message || 'No message provided'
    });
    
    if (data.success) {
      console.log('Extension authentication successful');
    } else {
      console.warn('Extension authentication failed:', data.message);
    }
  }
});

export type Profile = Database['public']['Tables']['profiles']['Row'];